import React, { useState, useEffect, useMemo } from 'react';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import RadioList from 'components/v2/RadioList';
import FormGroup from 'components/v2/FormGroup';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { Button } from 'react-bootstrap';
import { titleize } from 'underscore.string';
import Spinner from 'components/ui/Spinner';
import { getYards } from 'utils/YardAPIUtils';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import { isString, map, uniqBy } from 'lodash';
import Alert from 'components/ui/Alert';
const Loading = function () {
    return (React.createElement("div", { "data-testid": "bulkAddYardsLoader", className: "justify-center w-full flex flex-col" },
        React.createElement("div", { className: "align-middle" },
            React.createElement("span", { className: "text-5xl text-green" },
                React.createElement(Spinner, { showMessage: false })))));
};
const YardStopForm = ({ shipment, onClose }) => {
    const { id: shipmentId, market: { market_id: marketId }, } = shipment;
    const { t } = useTranslation();
    const stopIntentOptions = useMemo(() => [
        {
            id: 'deliver_full_pickup_full',
            name: t('DELIVERY_FULL_PICKUP_FULL'),
        },
        {
            id: 'deliver_empty_pickup_empty',
            name: t('DELIVERY_EMPTY_PICKUP_EMPTY'),
        },
    ], []);
    const [uiData, setUiData] = useState({
        intent: null,
        yardId: null,
        equipments: null,
        stop1_start: '',
        stop1_end: '',
        stop2_start: '',
        stop2_end: '',
    });
    const [isValid, setIsValid] = useState(false);
    const [yards, setYards] = useState(null);
    const [loading, setLoading] = useState(false);
    const stopsEquipments = map(shipment.stops, 'equipments');
    const hasEquipment = stopsEquipments.some((equipment) => Boolean(equipment.length));
    const equipments = uniqBy(stopsEquipments.filter((arr) => arr.length), 'equipment_id').map((equipment) => {
        if (hasEquipment) {
            const equipmentId = equipment[0].equipment_id;
            return {
                id: equipmentId,
                name: equipmentId,
            };
        }
        return {
            id: 0,
            name: t('NO_EQUIPMENT_IN_CURRENT_SHIPMENT'),
        };
    });
    const setDateToDatePicker = (type) => {
        return uiData[type] ? moment(uiData[type]).format('MM/DD/YY') : '';
    };
    const preparedYards = useMemo(() => {
        if (!yards) {
            return;
        }
        return yards.map((yard) => {
            return {
                id: yard.id,
                name: yard.nameInternal,
            };
        });
    }, [yards]);
    const preparePayload = () => {
        let stops = [
            {
                off_site: true,
                shipment: shipmentId,
            },
            {
                off_site: true,
                shipment: shipmentId,
            },
        ];
        const updateStops = (newData) => {
            stops = [
                Object.assign(Object.assign({}, stops[0]), newData),
                Object.assign(Object.assign({}, stops[1]), newData),
            ];
        };
        Object.keys(uiData).forEach((key) => {
            const isStartField = key.includes('start');
            const isEndField = key.includes('end');
            const value = uiData[key];
            if (isStartField || isEndField) {
                const stopNumber = key.replace(/\D/g, '');
                const changeItemNumber = stopNumber - 1;
                const restItemNumber = 2 - stopNumber; // get 0 or 1
                stops[changeItemNumber] = Object.assign(Object.assign({}, stops[changeItemNumber]), { [`window_${isStartField ? 'start' : 'end'}`]: value });
                stops[restItemNumber] = Object.assign({}, stops[restItemNumber]);
            }
            else if (key === 'intent') {
                const empty = value === 'deliver_empty_pickup_empty';
                stops = [
                    Object.assign(Object.assign({}, stops[0]), { action: 'deliver', empty }),
                    Object.assign(Object.assign({}, stops[1]), { action: 'pickup', empty }),
                ];
            }
            else if (key === 'yardId') {
                const { contactName: name, address } = yards.find((yard) => yard.id === value);
                const yardRelatedFields = {
                    yardId: value,
                    location: {
                        name,
                        address,
                    },
                };
                updateStops(yardRelatedFields);
            }
            else if (key === 'equipments') {
                const equipments = [value];
                updateStops({ equipments });
            }
        });
        return {
            jobId: shipmentId,
            stops,
        };
    };
    const updateModel = (field, value) => {
        setUiData((data) => {
            return Object.assign(Object.assign({}, data), { [field]: value });
        });
    };
    const validateData = () => Object.values(uiData).every((item) => Boolean(item));
    const handleSave = async () => {
        setLoading(true);
        const response = await ShipmentWebAPIUtils.addYardStop([preparePayload()]);
        setLoading(false);
        if (response.success) {
            onClose();
        }
    };
    const showPortProWarning = () => {
        var _a;
        const { carrier } = shipment;
        return (_a = carrier === null || carrier === void 0 ? void 0 : carrier.portpro) === null || _a === void 0 ? void 0 : _a.enabled;
    };
    useEffect(() => {
        const fetchYards = async (sortBy, sortOrder, skip, limit, yardInput = {
            id: null,
            marketId: null,
            nameInternal: null,
            status: null,
        }) => {
            return getYards(sortBy, sortOrder, skip, limit, yardInput);
        };
        async function receiveYards() {
            const yardData = await fetchYards('nameInternal', 'ascend', null, null, {
                id: null,
                marketId,
                nameInternal: null,
                status: 'active',
            });
            setYards(yardData.data.getYards.yards);
        }
        receiveYards();
    }, []);
    useEffect(() => {
        setIsValid(validateData());
    }, [uiData]);
    if (yards) {
        return (React.createElement("div", null,
            showPortProWarning() && (React.createElement("div", { className: "mb-2" },
                React.createElement(Alert, { className: "mt-2", type: "warning", title: t('ADD_STOP_PORTPRO_WARNING') }))),
            React.createElement("div", { className: "grid grid-cols-2 gap-4" },
                React.createElement(FormGroup, { label: t('YARD') },
                    React.createElement("div", { "data-testid": "addYardStopYard" },
                        React.createElement(CustomDropdown, { options: preparedYards, value: preparedYards.find((prepared) => prepared.id === uiData.yardId), nameKey: "name", valueKey: "id", onChange: (yard) => updateModel('yardId', yard.id) }))),
                React.createElement("div", { className: "col-span-full" },
                    React.createElement(FormGroup, { label: t('STOP_INTENT') },
                        React.createElement("div", { "data-testid": "addYardStopIntent" },
                            React.createElement(RadioList, { options: stopIntentOptions, selected: stopIntentOptions.find((intent) => intent.id === uiData.intent), onSelect: (intent) => updateModel('intent', intent.id) })))),
                React.createElement("div", { className: "col-span-full" },
                    React.createElement(FormGroup, { label: t('CONTAINER') },
                        React.createElement("div", { "data-testid": "addYardStopEquipment" },
                            React.createElement(RadioList, { label: t('SELECT'), options: equipments, disabled: !hasEquipment, selected: equipments.find((equipment) => equipment.id === uiData.equipments), onSelect: (equipments) => updateModel('equipments', equipments.id) })))),
                React.createElement(FormGroup, { label: t('STOP_1_START') },
                    React.createElement("div", { "data-testid": "addYardStopStop1_Start" },
                        React.createElement(DatePicker, { placeholderText: t('START'), value: setDateToDatePicker('stop1_start'), onChange: (value) => updateModel('stop1_start', moment(value).format()), className: "form-field", dropdownMode: "select", maxDate: uiData.stop1_end && isString(uiData.stop1_end)
                                ? moment(uiData.stop1_end)
                                : null }))),
                React.createElement(FormGroup, { label: t('STOP_1_END') },
                    React.createElement("div", { "data-testid": "addYardStopStop1_End" },
                        React.createElement(DatePicker, { placeholderText: t('END'), value: setDateToDatePicker('stop1_end'), onChange: (value) => updateModel('stop1_end', moment(value).format()), className: "form-field", dropdownMode: "select", minDate: uiData.stop1_start && isString(uiData.stop1_start)
                                ? moment(uiData.stop1_start)
                                : null }))),
                React.createElement(FormGroup, { label: t('STOP_2_START') },
                    React.createElement("div", { "data-testid": "addYardStopStop2_Start" },
                        React.createElement(DatePicker, { dateFormat: "YYYY-MM-DD", placeholderText: t('START'), value: setDateToDatePicker('stop2_start'), onChange: (value) => updateModel('stop2_start', moment(value).format()), className: "form-field", dropdownMode: "select", maxDate: uiData.stop2_end && isString(uiData.stop2_end)
                                ? moment(uiData.stop2_end)
                                : null }))),
                React.createElement(FormGroup, { label: t('STOP_2_END') },
                    React.createElement("div", { "data-testid": "addYardStopStop2_End" },
                        React.createElement(DatePicker, { dateFormat: "YYYY-MM-DD", placeholderText: t('END'), value: setDateToDatePicker('stop2_end'), onChange: (value) => updateModel('stop2_end', moment(value).format()), className: "form-field", dropdownMode: "select", minDate: uiData.stop2_start && isString(uiData.stop2_start)
                                ? moment(uiData.stop2_start)
                                : null })))),
            React.createElement("div", { "data-testid": "addYardStopControls", className: "col-span-full flex justify-end gap-3 mt-4" },
                React.createElement(Button, { "data-testid": "addYardStopCancel", onClick: onClose }, t('CANCEL')),
                React.createElement(Button, { "data-testid": "addYardStopAddStop", disabled: !isValid || loading, bsStyle: "primary", onClick: handleSave }, loading ? (React.createElement(LoadingCircle, { text: t('LOADING') })) : (titleize(t('ADD_STOPS')))))));
    }
    return React.createElement(Loading, null);
};
export default YardStopForm;
